/**
 * File uploader component with drag-drop support
 * Integrates with CCMS SDK file upload service
 */

import { forwardRef, useRef, useState, DragEvent, ChangeEvent } from 'react';
import { Card, Button, Progress, Stack, Text, Group, Badge } from '@/components/ui';
import { IconUpload, IconX, IconFile } from '@tabler/icons-react';
import { useFileUpload } from '../hooks/useFileUpload';
import { formatFileSize } from '../../utils/formatting';
import type { FileUploaderProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * File uploader component with drag-drop support and progress tracking
 *
 * @example
 * ```tsx
 * <FileUploader
 *   domain="User"
 *   recordId="user-123"
 *   multiple
 *   maxSize={10 * 1024 * 1024} // 10MB
 *   accept={['image/*', 'application/pdf']}
 *   onUploadComplete={(files) => console.log('Uploaded:', files)}
 * />
 * ```
 */
export const FileUploader = forwardRef<HTMLDivElement, FileUploaderProps>(
  (props, ref) => {
    const {
      domain,
      recordId,
      multiple = false,
      maxSize = 100 * 1024 * 1024, // 100MB default
      accept = ['*/*'],
      onUploadComplete,
      onUploadError,
      onProgress,
      autoUpload = true,
      variant = 'dropzone',
      gradientVariant = 'primary',
      disabled = false,
      className,
      hideAcceptedTypesHint = false,
    } = props;

    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const {
      uploading,
      progress,
      upload,
      cancel,
      error,
    } = useFileUpload({
      domain,
      recordId,
      maxSize,
      allowedTypes: accept,
      onComplete: onUploadComplete,
      onError: onUploadError,
      // @ts-expect-error - Type mismatch between progress callback signatures
      onProgress,
    });

    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !uploading) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled || uploading) return;

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        if (autoUpload) {
          upload(files);
        }
      }
    };

    const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      console.log('[FileUploader] handleFileSelect triggered, files:', files?.length, 'autoUpload:', autoUpload);
      if (files && files.length > 0) {
        if (autoUpload) {
          upload(files).catch((err) => {
            console.error('[FileUploader] upload error:', err);
          });
        }
      }
    };

    const handleBrowseClick = () => {
      console.log('[FileUploader] handleBrowseClick, ref exists:', !!fileInputRef.current);
      fileInputRef.current?.click();
    };

    // Don't pass '*/*' as accept - some browsers ignore the file picker entirely
    const acceptString = accept[0] === '*/*' || accept[0] === '*' ? '' : accept.join(',');

    // Dropzone variant
    if (variant === 'dropzone') {
      const CardComponent = Card as any;
      return (
        <CardComponent
          ref={ref}
          variant="elevated"
          className={className}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{
            border: isDragging
              ? '2px dashed var(--mantine-color-blue-5)'
              : '2px dashed var(--mantine-color-gray-4)',
            backgroundColor: isDragging
              ? 'var(--mantine-color-blue-0)'
              : 'transparent',
            transition: 'all 0.2s ease',
            cursor: disabled || uploading ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
          onClick={!uploading && !disabled ? handleBrowseClick : undefined}
        >
          <Stack gap="md" align="center" style={{ padding: '2rem' }}>
            <IconUpload size={48} color={isDragging ? 'var(--mantine-color-blue-5)' : 'var(--mantine-color-gray-5)'} />

            <Stack gap="xs" align="center">
              <Text size="lg" fw={600} style={accessibilityStyles}>
                {uploading ? 'Uploading files...' : 'Drop files here or click to browse'}
              </Text>
              <Text size="sm" c="dimmed" style={accessibilityStyles}>
                {multiple ? 'Multiple files allowed' : 'Single file only'} • Max size:{' '}
                {formatFileSize(maxSize)}
              </Text>
              {accept[0] !== '*/*' && !hideAcceptedTypesHint && (
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Accepted types: {accept.join(', ')}
                </Text>
              )}
            </Stack>

            {uploading && (
              <Stack gap="xs" style={{ width: '100%', maxWidth: 400 }}>
                {progress.map((p, idx) => (
                  <Stack key={idx} gap={4}>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <IconFile size={16} />
                        <Text size="sm" truncate style={{ maxWidth: 200, ...accessibilityStyles }}>
                          {p.fileName}
                        </Text>
                      </Group>
                      <Badge
                        size="sm"
                        variant={
                          p.status === 'completed'
                            ? 'filled'
                            : p.status === 'failed'
                            ? 'light'
                            : 'outline'
                        }
                        gradientVariant={
                          p.status === 'completed'
                            ? 'success'
                            : p.status === 'failed'
                            ? 'danger'
                            : 'primary'
                        }
                      >
                        {p.status}
                      </Badge>
                    </Group>
                    {p.status !== 'pending' && (
                      <Progress
                        value={p.progress}
                        gradientVariant={
                          p.status === 'failed' ? 'danger' : gradientVariant
                        }
                        size="sm"
                      />
                    )}
                    {p.error && (
                      <Text size="xs" c="red" style={accessibilityStyles}>
                        {p.error}
                      </Text>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}

            {error && (
              <Text size="sm" c="red" style={accessibilityStyles}>
                {error.message}
              </Text>
            )}

            {uploading && (
              <Button variant="light" gradientVariant="danger" size="sm" onClick={cancel}>
                <IconX size={16} style={{ marginRight: 4 }} />
                Cancel Upload
              </Button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple={multiple}
              accept={acceptString}
              onChange={handleFileSelect}
              // The input lives inside the clickable dropzone Card. Without this,
              // the programmatic fileInputRef.current.click() bubbles back up to
              // the Card's onClick → handleBrowseClick → .click() again, an
              // infinite recursion that stops the OS file dialog from ever opening
              // (drag-drop still worked because it never hits the click path).
              onClick={(e) => e.stopPropagation()}
              disabled={disabled || uploading}
              style={{ display: 'none' }}
            />
          </Stack>
        </CardComponent>
      );
    }

    // Button variant
    if (variant === 'button') {
      return (
        <div ref={ref} className={className}>
          <Button
            variant="gradient"
            gradientVariant={gradientVariant}
            leftSection={<IconUpload size={16} />}
            onClick={handleBrowseClick}
            disabled={disabled || uploading}
            loading={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Files'}
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            multiple={multiple}
            accept={acceptString}
            onChange={handleFileSelect}
            disabled={disabled || uploading}
            style={{ display: 'none' }}
          />

          {uploading && progress.length > 0 && (
            <Stack gap="xs" mt="md">
              {progress.map((p, idx) => (
                <div key={idx}>
                  <Text size="sm" style={accessibilityStyles}>{p.fileName}</Text>
                  <Progress value={p.progress} size="sm" gradientVariant={gradientVariant} />
                </div>
              ))}
            </Stack>
          )}

          {error && (
            <Text size="sm" c="red" mt="xs" style={accessibilityStyles}>
              {error.message}
            </Text>
          )}
        </div>
      );
    }

    // Inline variant
    return (
      <div ref={ref} className={className}>
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={acceptString}
          onChange={handleFileSelect}
          disabled={disabled || uploading}
        />

        {uploading && progress.length > 0 && (
          <Stack gap="xs" mt="sm">
            {progress.map((p, idx) => (
              <div key={idx}>
                <Text size="sm" style={accessibilityStyles}>{p.fileName}</Text>
                <Progress value={p.progress} size="sm" />
              </div>
            ))}
          </Stack>
        )}
      </div>
    );
  }
);

FileUploader.displayName = 'FileUploader';

export default FileUploader;
